import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ConversationParticipant, Typing } from './constants/types'
import QuestionsSidePanel from './QuestionsSidePanel'
import { State } from '../../dataStore/reducers/reducerType'
import username from '../../lib/username'
import { ParticipantInformationInterface } from '../../dataStore/reducers/docs/syncUi/types'
import { QuestionLoadingState } from './constants/enums'
import { addedMessageToConversation, setQuestionsAnsweredOrPriority, updateNumberOfMessagesRead } from '../../dataStore/actions/questions/questions'
import apiGateway from '../../services/apiGateway'
import { addEntry } from '../../lib/audit'
import getPresentersFromSeatingOrder from '../../dataStore/selectors/getPresentersFromSeatingOrder'
import selectObservers from '../../dataStore/selectors/selectObservers'

const getUsersName = (userId: string, participants: ParticipantInformationInterface[]) => {
  const participant = participants.find(participant => participant.id === userId)
  return participant ? participant.firstName + ' ' + participant.lastName : 'Unknown participant'
}

const QuestionsIndex = () => {
  const questions = useSelector((state: State) => state.questions)
  const userState = useSelector((state: State) => state.user)
  const participants = useSelector((state: State) => state.room.participantInformation)
  const presenters = useSelector((state: State) => getPresentersFromSeatingOrder(state))
  const observers = useSelector((state: State) => selectObservers(state))
  const { channelId, webcastId } = useSelector((state: State) => state.config)
  const [ typing, setTyping ] = useState<Typing[]>([])
  const [ sendMessageLoading, setSendMessageLoading ] = useState(QuestionLoadingState.IDLE)
  const [ showAnsweredNotification, setShowAnsweredNotification ] = useState(false)
  const dispatch = useDispatch<any>()

  const addReplyToConversation = async (text: string, conversationName: string, isAnswered: boolean) => {

    if (questions.conversationClientState) {

      try {

        setSendMessageLoading(QuestionLoadingState.LOADING)
        await questions.conversationClientState.sendMessage(conversationName, text,
          {
            userId: userState.BTID,
            userName: userState.data?.firstName + ' ' + userState.data?.lastName,
            companyName: userState.data?.companyName,
            photoId: userState.data?.photoId
          })
        setSendMessageLoading(QuestionLoadingState.SUCCESS)

        setTimeout(() => {
          setSendMessageLoading(QuestionLoadingState.IDLE)
        }, 2000)

        // Mark question as answered if isAnswered is false
        if (channelId && webcastId && !isAnswered) {
          try {

              addEntry('PresenterService', 'Q&AConversationConnection', `Setting question to answered after sending message for conversation: ${conversationName}`)

              const result = await apiGateway.setQuestionAnswered(channelId, webcastId, conversationName, true)

              if (result.status === 200) {

                addEntry('PresenterService', 'Q&AConversationConnection', `Successfully set question to answered after sending message for conversation: ${conversationName}`)

                setShowAnsweredNotification(true)

                setTimeout(() => {
                  setShowAnsweredNotification(false)
                }, 2000)

                dispatch(setQuestionsAnsweredOrPriority(conversationName, false))
              }

          } catch (e) {

            addEntry('PresenterService', 'Q&AConversationConnection', `Could not get set question as answered`)

          }
        }

        return true

      } catch (e) {

        setSendMessageLoading(QuestionLoadingState.ERROR)
        addEntry('PresenterService', 'Q&AConversationConnection', `Could not get send the message`)

        return false

      }

    }

  }

  const handleKeyDownEvent = async (event: React.KeyboardEvent, conversationName: string| undefined | null, text: string, isAnswered: boolean) => {
    if (!questions.conversationClientState || !conversationName) return

    questions.conversationClientState.typing(conversationName)
    if (event.key === 'Enter') {
      addReplyToConversation(text, conversationName, isAnswered)
    }
  }

  const setAllMessagesAsRead = async (conversationName: string | undefined | null) => {
    if (!questions.conversationClientState || !conversationName) return

    try {

      await questions.conversationClientState.setMessagesRead(conversationName)

      dispatch(updateNumberOfMessagesRead(conversationName))

    } catch (e) {

      addEntry('PresenterService', 'Q&AConversationConnection', `Could not set the messages to read`)

    }
  }

  const resetSendQuestionLoadingState = () => setSendMessageLoading(QuestionLoadingState.IDLE)

  const handleQuestionIconClick = async (conversationName: string, settingPriority: boolean, setToTrueOrFalse: boolean) => {
    if (channelId && webcastId) {
      try {

        addEntry('PresenterService', 'Q&AConversationConnection', `Setting question ${settingPriority ? 'priority' : 'answered'} to ${setToTrueOrFalse} for conversation ${conversationName}`)

        const result = !settingPriority ? await apiGateway.setQuestionAnswered(channelId, webcastId, conversationName, setToTrueOrFalse)
          : await apiGateway.setQuestionPriority(channelId, webcastId, conversationName, setToTrueOrFalse)

        if (result.status === 200) {

          addEntry('PresenterService', 'Q&AConversationConnection', `Successfully set question ${settingPriority ? 'priority' : 'answered'} to ${setToTrueOrFalse} for conversation ${conversationName}`)
          setShowAnsweredNotification(true)

          setTimeout(() => {
            setShowAnsweredNotification(false)
          }, 2000)
        }

        return true

      } catch (e) {

        setTimeout(() => {
          setShowAnsweredNotification(false)
        }, 2000)

        addEntry('PresenterService', 'Q&AConversationConnection', `Could not get set question priority`)
        return false

      }
    }
  }

  const handleParticipantTyping = useCallback((participant, started: boolean) => {
    if (!started) return setTyping(typing.filter(obj => participant.conversationName !== obj.conversationName && participant.author !== obj.author))

    if (typing.length < 1 || typing.some(obj => participant.conversationName !== obj.conversationName && participant.author !== obj.author)) {
      setTyping([...typing, {
        authorName: getUsersName(username.getPureUsername(participant.author), participants),
        author: participant.author,
        conversationName: participant.conversationName
      }])
    }
  }, [participants, typing])

  const handleTypingStarted = useCallback((participant: ConversationParticipant) => {
    handleParticipantTyping(participant, true)
  }, [handleParticipantTyping])

  const handleTypingEnded = useCallback((participant: ConversationParticipant) => {
    handleParticipantTyping(participant, false)
  }, [handleParticipantTyping])

  const handleMessageAdded = useCallback(async (participant: ConversationParticipant) => {

    if (!questions.conversationClientState) return
    let messageAddedByAudience = false

    const authorPureUsername = username.getPureUsername(participant.author)
    if (!presenters.some(presenter => presenter.BTID === authorPureUsername) && !observers.some(observer => observer.id === authorPureUsername) &&
      questions.result.some(question => question.uniqueConversationName === participant.conversationName && question.isAnswered)) {
      if (channelId && webcastId) {
        try {
          const result = await apiGateway.setQuestionAnswered(channelId, webcastId, participant.conversationName, false)
          if (result.status === 200) messageAddedByAudience = true
        } catch (e) {
          addEntry('PresenterService', 'Q&AConversationConnection', `Could not get set question as answered`)
          return null
        }
      }
    }

    dispatch(addedMessageToConversation(participant.conversationName, messageAddedByAudience))

  }, [dispatch, questions.conversationClientState, channelId, webcastId, questions.result])

  useEffect(() => {

    if (!questions.conversationClientState) return

    questions.conversationClientState.on('typingStarted', handleTypingStarted)
    questions.conversationClientState.on('typingEnded', handleTypingEnded)
    questions.conversationClientState.on('messageAdded', handleMessageAdded)

    return () => {
      questions.conversationClientState.off('typingStarted', handleTypingStarted)
      questions.conversationClientState.off('typingEnded', handleTypingEnded)
      questions.conversationClientState.off('messageAdded', handleMessageAdded)
    }

  }, [questions, handleTypingStarted, handleTypingEnded, handleMessageAdded, questions.result])

  return <QuestionsSidePanel
            questions={questions.result}
            questionsLoading={questions.loading}
            typing={typing}
            sendMessageLoading={sendMessageLoading}
            showAnsweredNotification={showAnsweredNotification}
            handleQuestionIconClick={handleQuestionIconClick}
            addReplyToConversation={addReplyToConversation}
            handleKeyDownEvent={handleKeyDownEvent}
            setAllMessagesAsRead={setAllMessagesAsRead}
            resetSendQuestionLoadingState={resetSendQuestionLoadingState} />
}

export default QuestionsIndex